import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingDown, Target, Shield, Star, CheckCircle2, User, Heart } from 'lucide-react';
import { entreprisesContent, images } from '../data/mock';

const iconMap = {
  TrendingDown,
  Target,
  Shield,
  Star,
  User,
  Heart,
};

const Entreprises = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={entreprisesContent.heroImage}
            alt="Bien-être en entreprise"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="max-w-2xl">
            <h1 className="font-heading text-4xl md:text-5xl font-medium text-foreground mb-6">
              {entreprisesContent.title}
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed mb-8">
              {entreprisesContent.subtitle}
            </p>
            <Link to="/contact" className="btn-primary bg-primary text-white hover:bg-primary/90">
              Demander un devis entreprise
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Employer Benefits Section - ENHANCED with 5 items */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-4">
              Avantages pour l'employeur
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Investir dans le bien-être de vos équipes, c'est investir dans 
              la performance de votre entreprise.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {entreprisesContent.employerBenefits.map((benefit, index) => {
              const Icon = iconMap[benefit.icon];
              return (
                <div
                  key={index}
                  className="p-6 bg-white border border-border card-hover text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-5 bg-primary/10 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-medium text-foreground mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Logistics Section */}
      <section className="py-24 section-gradient">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-6">
                Logistique simplifiée
              </h2>
              <p className="text-muted-foreground mb-10 leading-relaxed">
                L'intervention Amma assis en entreprise est conçue pour 
                s'intégrer facilement à votre organisation.
              </p>
              <div className="space-y-6">
                {entreprisesContent.logistics.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-heading text-lg font-medium text-foreground mb-1">
                        {item.title}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-10 border border-border">
              <h3 className="font-heading text-2xl font-medium text-foreground mb-6">
                Ce dont vous avez besoin
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-medium">1</span>
                  </div>
                  <p className="text-muted-foreground pt-1">
                    Un espace calme de <strong className="text-foreground">4-5 m²</strong>
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-medium">2</span>
                  </div>
                  <p className="text-muted-foreground pt-1">
                    Un planning des <strong className="text-foreground">créneaux salariés</strong>
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-medium">3</span>
                  </div>
                  <p className="text-muted-foreground pt-1">
                    <strong className="text-foreground">C'est tout !</strong> J'apporte le matériel.
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-24 bg-primary/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            <div className="p-8">
              <div className="font-heading text-5xl font-medium text-primary mb-2">
                15-20
              </div>
              <p className="text-muted-foreground">minutes par séance</p>
            </div>
            <div className="p-8">
              <div className="font-heading text-5xl font-medium text-primary mb-2">
                100%
              </div>
              <p className="text-muted-foreground">des salariés satisfaits</p>
            </div>
            <div className="p-8">
              <div className="font-heading text-5xl font-medium text-primary mb-2">
                0
              </div>
              <p className="text-muted-foreground">perturbation du travail</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - ENHANCED */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-6">
            Prêt à améliorer le quotidien de vos équipes ?
          </h2>
          <p className="text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
            Discutons ensemble de la meilleure façon d'intégrer le Amma assis 
            dans votre entreprise.
          </p>
          <Link to="/contact" className="btn-primary bg-primary text-white hover:bg-primary/90">
            Demander un devis entreprise
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Entreprises;
